
(function () {
  'use strict';

  if (window.__customAbFacetsLoaded) return;
  window.__customAbFacetsLoaded = true;

  /* ------------------------------------------------------------------------
     1. Constants
     ------------------------------------------------------------------------ */

  const SELECTORS = {
    root: '[data-custom-ab-root]',
    form: '[data-custom-ab-form]',
    results: '[data-custom-ab-results]',
    panel: '[data-custom-ab-panel]',
    overlay: '[data-custom-ab-overlay]',
    open: '[data-custom-ab-open]',
    close: '[data-custom-ab-close]',
    apply: '[data-custom-ab-apply]',
    clear: '[data-custom-ab-clear]',
    link: '[data-custom-ab-link]',
    range: '[data-custom-ab-range]',
    priceFields: '[data-custom-ab-price-fields]'
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // How long typing in a price field pauses before the filter applies.
  const PRICE_DEBOUNCE_MS = 600;

  // Must match --custom-ab-accordion-ms in the stylesheet.
  const ACCORDION_MS = 280;

  const FALLBACK_GRID = '.collection-matrix, .search-matrix, .product-list';

  function debounce(fn, waitMs) {
    let timer;
    return function () {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, waitMs);
    };
  }

  function toNumber(value, fallback) {
    const n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  // Shopify's money format decides how digits are grouped: IDR reads
  // "1.318.900" where USD reads "1,318,900". The storefront locale is no help
  // (an English storefront can sell in IDR), so the markup ships a probe --
  // 1112223.33 pushed through the same `money` filter that renders the price
  // chip and the product cards -- and the separator is read back off it. The
  // group separator is the one with exactly three digits behind it; anything
  // else is the decimal point.
  function priceGroupSeparator(input) {
    const fields = input.closest(SELECTORS.priceFields);
    const probe = fields ? fields.getAttribute('data-money-probe') : '';
    if (probe) {
      const match = probe.match(/\d(\D)(\d{3})(?:\1\d{3})*(?!\d)/);
      // A probe with no grouping at all means the shop groups with nothing.
      return match ? match[1] : '';
    }
    return localeGroupSeparator();
  }

  // Only reached if the probe never rendered. Guessing from the page language
  // beats hard-coding a comma, which quietly disagrees with every European and
  // Indonesian format.
  let cachedLocaleSeparator = null;
  function localeGroupSeparator() {
    if (cachedLocaleSeparator !== null) return cachedLocaleSeparator;
    cachedLocaleSeparator = ',';
    try {
      const lang = document.documentElement.getAttribute('lang') || undefined;
      const parts = new Intl.NumberFormat(lang).formatToParts(1112223);
      const group = parts.filter(function (part) {
        return part.type === 'group';
      })[0];
      cachedLocaleSeparator = group ? group.value : '';
    } catch (error) {
      /* Intl missing or the lang tag is junk — the comma default stands. */
    }
    return cachedLocaleSeparator;
  }

  function priceDigits(value) {
    return String(value == null ? '' : value).replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  }

  function priceGrouped(value, separator) {
    const digits = priceDigits(value);
    if (!digits || !separator) return digits;
    return digits.replace(/\B(?=(\d{3})+$)/g, separator);
  }

  // The visible field carries grouped digits, so the bare amount the filter
  // param is built from lives on the hidden input beside it.
  function priceRawInput(input) {
    const field = input.closest('.custom-ab-price__field');
    return field ? field.querySelector('[data-custom-ab-price-raw]') : null;
  }

  function setPriceValue(input, value, regroup) {
    const raw = priceDigits(value);
    const hidden = priceRawInput(input);
    if (hidden) hidden.value = raw;
    if (regroup) input.value = raw ? priceGrouped(raw, priceGroupSeparator(input)) : '';
  }

  function formToQuery(form) {
    const params = new URLSearchParams(new FormData(form));
    const cleaned = new URLSearchParams();
    params.forEach(function (value, key) {
      if (value === null || String(value).trim() === '') return;
      if (key === 'page') return;
      cleaned.append(key, value);
    });
    return cleaned.toString();
  }


  let scrollLockY = 0;
  let scrollLockActive = false;

  function lockBodyScroll() {
    if (scrollLockActive) return;
    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = '-' + scrollLockY + 'px';
    document.body.classList.add('custom-ab-scroll-locked');
    scrollLockActive = true;
  }

  function unlockBodyScroll() {
    if (!scrollLockActive) return;
    document.body.classList.remove('custom-ab-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, scrollLockY);
    scrollLockActive = false;
  }

  function setupFacets(root) {

    const sectionId = root.getAttribute('data-custom-ab-section');

    let drawerIsOpen = false; //   is the mobile drawer showing right now?
    let lastFocused = null; //     element to give focus back to on close
    let pendingScroll = false; //  scroll once the drawer closes
    let fetchController = null; // in-flight fetch, aborted by newer requests
    let fetchToken = 0; //         guards against stale responses winning

    const applyDebounced = debounce(applyFromForm, PRICE_DEBOUNCE_MS);

    // The form and results are re-queried on demand because their contents
    // are replaced on every update.
    function getForm() {
      return root.querySelector(SELECTORS.form);
    }
    function getPanel() {
      return root.querySelector(SELECTORS.panel);
    }
    function getResults() {
      return root.querySelector(SELECTORS.results);
    }

    /* ---------- 4.2 Events — everything is wired here ---------- */

    function bindEvents() {

      root.addEventListener('click', onRootClick, true);

      root.addEventListener('change', function (event) {
        const el = event.target;
        if (!el) return;
        if (
          el.matches('input[type="checkbox"][name]') ||
          el.matches('select[name="sort_by"]') ||
          el.matches('[data-custom-ab-range-min], [data-custom-ab-range-max]')
        ) {
          // Checkbox toggled, sort picked, or a slider handle released.
          applyFromForm();
        }
      });

      root.addEventListener('input', function (event) {
        const el = event.target;
        if (!el) return;
        if (el.matches('[data-custom-ab-range-min], [data-custom-ab-range-max]')) {
          onRangeInput(el);
        } else if (el.matches('[data-custom-ab-price]')) {
          onPriceInput(el);
          applyDebounced();
        }
      });

      root.addEventListener('submit', function (event) {
        if (event.target && event.target.matches(SELECTORS.form)) {
          event.preventDefault();
          closeDrawer();
          applyFromForm();
        }
      });

      // Regrouping waits for blur -- doing it per keystroke drags the caret.
      root.addEventListener('focusout', function (event) {
        const el = event.target;
        if (el && el.matches && el.matches('[data-custom-ab-price]')) {
          onPriceBlur(el);
        }
      });

      document.addEventListener('keydown', onKeydown);
      // Backstop for the overlay: closes the drawer on any tap outside it.
      document.addEventListener('click', onOutsideClick);
      window.addEventListener('popstate', function () {
        render(window.location.href, false);
      });
      window.addEventListener('resize', debounce(onResize, 150));
    }


    function onRootClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;

      if (target.closest(SELECTORS.open)) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (target.closest(SELECTORS.close) || target.closest(SELECTORS.overlay)) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (target.closest(SELECTORS.apply)) {
        // Results are already live; Apply just dismisses the drawer.
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (target.closest(SELECTORS.clear)) {
        event.preventDefault();
        clearAll();
        return;
      }

      const summary = target.closest('.custom-ab-group__summary');
      if (summary) {
        event.preventDefault();
        toggleGroup(summary);
        return;
      }

      // Active-filter chips and clear links.
      const chip = target.closest(SELECTORS.link);
      if (chip && chip.href) {
        event.preventDefault();
        event.stopPropagation();
        render(chip.href, true, true);
        return;
      }

      const results = getResults();
      if (!results || !results.contains(target)) return;

      const link = target.closest('a[href]');
      if (!link) return;
      // The theme's own load-more and our module's button opt out here.
      if (link.closest('.js-load-more') || link.hasAttribute('data-custom-ab-ignore')) return;
      if (link.href.indexOf('page=') === -1) return;
      if (link.hostname !== window.location.hostname) return;

      event.preventDefault();
      event.stopPropagation();
      render(link.href, true, true);
    }

    function onOutsideClick(event) {
      if (!drawerIsOpen) return;

      const panel = getPanel();
      const target = event.target;
      if (!panel || !target || !target.closest) return;

      if (panel.contains(target)) return; //         inside the drawer
      if (target.closest(SELECTORS.open)) return; // the trigger that opened it

      closeDrawer();
    }

    function onKeydown(event) {
      if (!drawerIsOpen) return;
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        closeDrawer();
      } else if (event.key === 'Tab' || event.keyCode === 9) {
        trapFocus(event);
      }
    }

    function onResize() {

      if (!drawerIsOpen) return;
      const trigger = root.querySelector(SELECTORS.open);
      if (trigger && trigger.offsetParent === null) closeDrawer();
    }

    /* ---------- 4.4 Drawer ---------- */

    function openDrawer() {
      const panel = getPanel();
      const overlay = root.querySelector(SELECTORS.overlay);
      if (!panel) return;

      lastFocused = document.activeElement;
      drawerIsOpen = true;

      panel.classList.add('is-open');
      if (overlay) {
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
      }

      const trigger = root.querySelector(SELECTORS.open);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      lockBodyScroll();

      const closeBtn = panel.querySelector(SELECTORS.close);
      if (closeBtn) {
        requestAnimationFrame(function () {
          closeBtn.focus();
        });
      }
    }

    function closeDrawer() {
      if (!drawerIsOpen) return;

      const panel = getPanel();
      const overlay = root.querySelector(SELECTORS.overlay);

      drawerIsOpen = false;
      if (panel) panel.classList.remove('is-open');
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
      }

      const trigger = root.querySelector(SELECTORS.open);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      unlockBodyScroll();

      if (lastFocused && lastFocused.focus) {
        lastFocused.focus();
      } else if (trigger) {
        trigger.focus();
      }

      if (pendingScroll) {
        pendingScroll = false;
        scrollToResults();
      }
    }

    // Keep Tab / Shift+Tab inside the open drawer.
    function trapFocus(event) {
      const panel = getPanel();
      if (!panel) return;

      const nodes = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }


    function toggleGroup(summary) {
      const group = summary.closest('.custom-ab-group');
      if (!group) return;

      const content = group.querySelector('.custom-ab-group__content');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!content || reduceMotion) {
        group.open = !group.open;
        return;
      }

      // A second click mid-animation must not leave a stale timer behind.
      if (content.customAbTimer) {
        clearTimeout(content.customAbTimer);
        content.customAbTimer = null;
      }

      if (group.open) {
        // Closing: fix the height, force a reflow, then transition to 0.
        content.style.height = content.scrollHeight + 'px';
        content.offsetHeight;
        content.style.height = '0px';
        content.customAbTimer = setTimeout(function () {
          group.open = false;
          content.style.height = '';
          content.customAbTimer = null;
        }, ACCORDION_MS);
      } else {
        // Opening: start at 0 and transition up to the content's height.
        group.open = true;
        content.style.height = '0px';
        content.offsetHeight;
        content.style.height = content.scrollHeight + 'px';
        content.customAbTimer = setTimeout(function () {
          content.style.height = '';
          content.customAbTimer = null;
        }, ACCORDION_MS);
      }
    }

    function paintAllRanges() {
      root.querySelectorAll(SELECTORS.range).forEach(paintRange);
    }

    function paintRange(wrap) {
      const minEl = wrap.querySelector('[data-custom-ab-range-min]');
      const maxEl = wrap.querySelector('[data-custom-ab-range-max]');
      const fill = wrap.querySelector('[data-custom-ab-range-fill]');
      if (!minEl || !maxEl || !fill) return;

      const hi = toNumber(wrap.getAttribute('data-range-max'), 0);
      if (hi <= 0) return;

      const a = toNumber(minEl.value, 0);
      const b = toNumber(maxEl.value, hi);

      const width = wrap.getBoundingClientRect().width;
      const thumb = 18;

      if (!width) {
        // Not laid out yet (e.g. inside a closed group) — percentages do.
        fill.style.left = (a / hi) * 100 + '%';
        fill.style.right = 100 - (b / hi) * 100 + '%';
        return;
      }

      const usable = width - thumb;
      const leftPx = thumb / 2 + (a / hi) * usable;
      const rightPx = thumb / 2 + (b / hi) * usable;

      fill.style.left = leftPx + 'px';
      fill.style.right = width - rightPx + 'px';
    }

    // Dragging a handle: keep the two from crossing, then mirror into the
    // number inputs so the form carries the real filter params.
    function onRangeInput(input) {
      const wrap = input.closest(SELECTORS.range);
      if (!wrap) return;

      const minEl = wrap.querySelector('[data-custom-ab-range-min]');
      const maxEl = wrap.querySelector('[data-custom-ab-range-max]');
      const hi = toNumber(wrap.getAttribute('data-range-max'), 0);
      let a = toNumber(minEl.value, 0);
      let b = toNumber(maxEl.value, hi);

      if (a > b) {
        if (input === minEl) {
          a = b;
          minEl.value = a;
        } else {
          b = a;
          maxEl.value = b;
        }
      }

      const group = wrap.parentNode;
      const priceMin = group.querySelector('[data-custom-ab-price-min]');
      const priceMax = group.querySelector('[data-custom-ab-price-max]');

      if (priceMin) setPriceValue(priceMin, a <= 0 ? '' : a, true);
      if (priceMax) setPriceValue(priceMax, b >= hi - 0.01 ? '' : b, true);

      paintRange(wrap);
    }

    // Typing in a price input: keep the hidden filter param and the handles in
    // sync. The digits are left exactly as typed until the field loses focus.
    function onPriceInput(input) {
      setPriceValue(input, input.value, false);
      mirrorPriceToRange(input);
    }

    // Leaving a price input: clamp it to the range, then regroup the digits.
    function onPriceBlur(input) {
      const group = input.closest('.custom-ab-group__content') || root;
      const wrap = group.querySelector(SELECTORS.range);
      const hi = wrap ? toNumber(wrap.getAttribute('data-range-max'), 0) : 0;

      let raw = priceDigits(input.value);
      if (raw !== '' && hi > 0 && toNumber(raw, 0) > hi) raw = String(hi);

      setPriceValue(input, raw, true);
      mirrorPriceToRange(input);
    }

    function mirrorPriceToRange(input) {
      const group = input.closest('.custom-ab-group__content') || root;
      const wrap = group.querySelector(SELECTORS.range);
      if (!wrap) return;

      const hi = toNumber(wrap.getAttribute('data-range-max'), 0);
      const minEl = wrap.querySelector('[data-custom-ab-range-min]');
      const maxEl = wrap.querySelector('[data-custom-ab-range-max]');
      const hidden = priceRawInput(input);
      const raw = hidden ? hidden.value : priceDigits(input.value);

      if (input.hasAttribute('data-custom-ab-price-min') && minEl) {
        minEl.value = toNumber(raw, 0);
      } else if (input.hasAttribute('data-custom-ab-price-max') && maxEl) {
        maxEl.value = toNumber(raw, hi);
      }

      paintRange(wrap);
    }

    // Applies the shop's grouping to whatever the server just rendered, values
    // and placeholders alike. Idempotent: the digits are re-read each time.
    function formatAllPrices() {
      root.querySelectorAll('[data-custom-ab-price]').forEach(function (input) {
        if (input.value) setPriceValue(input, input.value, true);
        if (input.placeholder) {
          input.placeholder = priceGrouped(input.placeholder, priceGroupSeparator(input));
        }
      });
    }

    // Read the form and render the matching URL.
    function applyFromForm() {
      const form = getForm();
      if (!form) return;

      const action = form.getAttribute('action') || window.location.pathname;
      const pathname = action.split('?')[0];
      const query = formToQuery(form);

      // Every filter change scrolls the results back into view.
      render(query ? pathname + '?' + query : pathname, true, true);
    }

    function clearAll() {
      const form = getForm();
      if (!form) return;

      form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
        input.checked = false;
      });
      form.querySelectorAll('[data-custom-ab-price], [data-custom-ab-price-raw]').forEach(function (input) {
        input.value = '';
      });
      form.querySelectorAll(SELECTORS.range).forEach(function (wrap) {
        const hi = toNumber(wrap.getAttribute('data-range-max'), 0);
        const minEl = wrap.querySelector('[data-custom-ab-range-min]');
        const maxEl = wrap.querySelector('[data-custom-ab-range-max]');
        if (minEl) minEl.value = 0;
        if (maxEl) maxEl.value = hi;
      });

      paintAllRanges();
      applyFromForm();
    }

    // Fetch the section for `url` and swap the new markup in.
    function render(url, pushState, scrollToTop) {
      if (fetchController) fetchController.abort();
      fetchController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      const token = ++fetchToken;

      const target = new URL(url, window.location.origin);
      const cleanUrl = target.pathname + target.search;

      const fetchUrl = new URL(target.href);
      fetchUrl.searchParams.set('section_id', sectionId);

      root.classList.add('is-loading');

      const options = { headers: { 'X-Requested-With': 'XMLHttpRequest' } };
      if (fetchController) options.signal = fetchController.signal;

      fetch(fetchUrl.toString(), options)
        .then(function (response) {
          if (!response.ok) throw new Error('Section render failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (token !== fetchToken) return;
          swap(html, cleanUrl, pushState, scrollToTop);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (token !== fetchToken) return;
          // Never leave the shopper stuck — fall back to a real navigation.
          window.location.href = cleanUrl;
        })
        .finally(function () {
          if (token !== fetchToken) return;
          root.classList.remove('is-loading');
          fetchController = null;
        });
    }

    // Put the freshly rendered results + filter panel into the page.
    function swap(html, cleanUrl, pushState, scrollToTop) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const newResults = parsed.querySelector(SELECTORS.results);
      const currentResults = getResults();

      if (!newResults || !currentResults) {
        window.location.href = cleanUrl;
        return;
      }

      currentResults.innerHTML = newResults.innerHTML;

      const newForm = parsed.querySelector(SELECTORS.form);
      const currentForm = getForm();

      if (newForm && currentForm) {
        const openState = Array.from(
          currentForm.querySelectorAll('details.custom-ab-group'),
          function (group) {
            return group.open;
          }
        );

        currentForm.innerHTML = newForm.innerHTML;

        currentForm.querySelectorAll('details.custom-ab-group').forEach(function (group, i) {
          if (typeof openState[i] === 'boolean') group.open = openState[i];
        });

        // innerHTML replacement drops the panel's open class — reapply.
        if (drawerIsOpen) {
          const panel = getPanel();
          const overlay = root.querySelector(SELECTORS.overlay);
          if (panel) panel.classList.add('is-open');
          if (overlay) {
            overlay.classList.add('is-open');
            overlay.setAttribute('aria-hidden', 'false');
          }
          const trigger = root.querySelector(SELECTORS.open);
          if (trigger) trigger.setAttribute('aria-expanded', 'true');
        }
      }

      markLinks();
      paintAllRanges();
      formatAllPrices();

      if (pushState && window.history && window.history.pushState) {
        window.history.pushState({ customAbFacets: true }, '', cleanUrl);
      }

      if (scrollToTop) {
        if (drawerIsOpen) {

          pendingScroll = true;
        } else {
          scrollToResults();
        }
      }

      document.dispatchEvent(
        new CustomEvent('custom-ab:updated', {
          bubbles: true,
          detail: { root: root, url: cleanUrl }
        })
      );

      reinitPagination();

      // Nudge lazy-loaders and grid scripts that only listen for these.
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('scroll'));
    }

    function scrollToResults() {
      const measured = parseFloat(
        window.getComputedStyle(root).getPropertyValue('--custom-ab-sticky-top')
      );
      const offset = (isNaN(measured) ? 80 : measured) + 10;

      const top = root.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: top < 0 ? 0 : top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'
      });
    }


    function markLinks() {
      const results = getResults();
      if (results) {
        results.querySelectorAll('a[href*="page="]').forEach(function (link) {
          if (link.closest('.js-load-more')) return;
          link.setAttribute('data-no-instant', '');
        });
      }
      root.querySelectorAll(SELECTORS.link).forEach(function (link) {
        link.setAttribute('data-no-instant', '');
      });
    }


    function ensureResults() {
      if (getResults()) return;

      const section = root.closest('[id^="shopify-section-"]') || document;
      const selector = root.getAttribute('data-custom-ab-results-selector') || FALLBACK_GRID;
      const grid = section.querySelector(selector);

      if (!grid || root.contains(grid)) return;

      const wrap = document.createElement('div');
      wrap.className = 'custom-ab-results';
      wrap.setAttribute('data-custom-ab-results', '');

      // Take the grid plus the pagination bits that follow it.
      const nodes = [grid];
      let next = grid.nextElementSibling;
      while (next) {
        if (
          next.matches('.load-more__icon, .js-load-more, .paginate') ||
          next.querySelector('.paginate, .js-load-more')
        ) {
          nodes.push(next);
          next = next.nextElementSibling;
        } else {
          break;
        }
      }

      nodes.forEach(function (node) {
        wrap.appendChild(node);
      });
      root.appendChild(wrap);
    }

    function measureStickyOffset() {
      let tallest = 0;
      const candidates = document.querySelectorAll(
        'header, [class*="header" i], [id*="header" i], [data-sticky-header]'
      );

      candidates.forEach(function (el) {
        const cs = window.getComputedStyle(el);
        if (cs.position !== 'sticky' && cs.position !== 'fixed') return;

        // Read the declared offset rather than the current rect: at scroll 0
        // a sticky header hasn't travelled to its pinned position yet.
        const stickTop = parseFloat(cs.top);
        if (isNaN(stickTop) || stickTop > 60) return;

        const height = el.getBoundingClientRect().height;
        if (!height) return;

        tallest = Math.max(tallest, stickTop + height);
      });

      // Ignore implausible measurements rather than pushing the card off-screen.
      if (tallest > 0 && tallest < window.innerHeight * 0.5) {
        root.style.setProperty('--custom-ab-sticky-top', Math.round(tallest) + 20 + 'px');
      }
    }

    function reinitPagination() {
      if (root.getAttribute('data-custom-ab-pagination') !== 'infinite_scroll') return;

      const results = getResults();
      if (!results) return;

      if (results.querySelector('.collection-matrix') && typeof window.enableInfiniteScroll === 'function') {
        window.enableInfiniteScroll();
      } else if (results.querySelector('.search-matrix') && typeof window.enableInfiniteSearchScroll === 'function') {
        window.enableInfiniteSearchScroll();
      }
    }


    ensureResults();
    bindEvents();
    markLinks();
    paintAllRanges();
    formatAllPrices();
    measureStickyOffset();
    root.setAttribute('data-custom-ab-init', 'true');
  }


  function init() {
    document.querySelectorAll(SELECTORS.root).forEach(function (root) {
      if (root.getAttribute('data-custom-ab-init') === 'true') return;
      setupFacets(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('page:loaded', init);
})();